import React from 'react'
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  BottomNavigation,
  BottomNavigationAction,
  useTheme,
  Paper,
} from '@mui/material'
import {
  Dashboard as DashboardIcon,
  Build as BuildIcon,
  Business as BusinessIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import HausviseLogo from '../resources/svg/hausvise-logo'
import { useResponsive, useResponsiveSpacing } from '../hooks/useResponsive'
import Breadcrumb from '../components/Breadcrumb'

/** Props for the UserLayout shell component. */
interface UserLayoutProps {
  children: React.ReactNode
}

/** Navigation item configuration for sidebar/bottom nav. */
interface NavigationItem {
  path: string
  label: string
  icon: React.ReactNode
  showOnMobile?: boolean // Display in bottom navigation on mobile
}

/**
 * Application shell with responsive navigation following NN Group breakpoint guidelines.
 *
 * Renders two navigation modes based on viewport width:
 * - **Mobile/Tablet** (< 1200px): Bottom navigation bar with primary routes
 * - **Laptop/Desktop** (≥ 1200px): Permanent left drawer with full navigation list
 *
 * Navigation items: Dashboard, Maintenance, Companies, Settings, Logout.
 * Company-scoped pages (Personnel, Meters, History, OperationalLog) are accessed
 * via nested routes under `/companies/:id/*` and are not in the main navigation.
 *
 * @remarks
 * Uses `useResponsive()` hook for NN Group breakpoints:
 * Mobile (< 500px), Tablet (500-1200px), Laptop (1200-1400px), Desktop (1400px+).
 * Drawer width: 220px. Bottom nav is a fixed `Paper` at viewport bottom.
 * Logout dispatches `logoutUser()` thunk and navigates to `/login`.
 *
 * @param props - `children` content rendered in the main area alongside navigation
 */
const UserLayout: React.FC<UserLayoutProps> = ({ children }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()

  // NN Group responsive breakpoints
  const { isMobile, showBottomNav, showPermanentDrawer } = useResponsive()
  const { contentPadding } = useResponsiveSpacing()

  // Navigation items configuration
  const navigationItems: NavigationItem[] = [
    {
      path: '/dashboard',
      label: t('navigation.dashboard'),
      icon: <DashboardIcon />,
      showOnMobile: true,
    },
    {
      path: '/maintenance',
      label: t('navigation.maintenance'),
      icon: <BuildIcon />,
      showOnMobile: true,
    },
    {
      path: '/companies',
      label: t('navigation.companies'),
      icon: <BusinessIcon />,
      showOnMobile: true,
    },
    // Note: Personnel, Meters, History, and OperationalLog are accessed per-company
    // at /companies/:id/* routes and should not be in main navigation.
    // They require a specific company context to function properly.
    // InvitationHistory is accessible from PersonnelManagement page.
  ]

  const handleLogout = () => {
    navigate('/logout')
  }

  const handleNavigation = (path: string) => {
    navigate(path)
  }

  const getCurrentPath = () => {
    // Match current path with navigation items
    const current = navigationItems.find(item => location.pathname === item.path || location.pathname.startsWith(item.path + '/'))
    return current?.path || '/dashboard'
  }

  const drawerWidth = (theme.mixins as any).drawerWidth || 280

  // Drawer content (for mobile hamburger menu and desktop sidebar)
  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* App branding in drawer */}

      <Divider />

      {/* Navigation list */}
      <List component='nav' sx={{ flex: 1, py: 1 }}>
        {navigationItems.map(item => (
          <ListItemButton
            key={item.path}
            selected={location.pathname === item.path}
            onClick={() => handleNavigation(item.path)}
            sx={{
              minHeight: 48, // Touch-friendly
              px: 2.5,
            }}>
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItemButton>
        ))}
      </List>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar
        position='fixed'
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.primary.main,
          color: '#ffffff',
        }}>
        <Toolbar>
          {/* Hausvise Logo */}
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              py: 0.5,
            }}>
            <HausviseLogo variant='white' />
          </Box>

          {/* Settings and Logout buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton size='large' aria-label={t('navigation.settings')} onClick={() => navigate('/settings')} color='inherit'>
              <SettingsIcon />
            </IconButton>
            <IconButton size='large' aria-label={t('auth.logout')} onClick={handleLogout} color='inherit'>
              <LogoutIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Navigation Drawer (laptop/desktop only) */}
      {showPermanentDrawer && (
        <Drawer
          variant='permanent'
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              mt: `${theme.mixins.toolbar.minHeight}px`,
            },
          }}
          open>
          {drawerContent}
        </Drawer>
      )}

      {/* Main content area */}
      <Box
        component='main'
        sx={{
          flexGrow: 1,
          p: contentPadding,
          mt: `${theme.mixins.toolbar.minHeight}px`,
          ml: showPermanentDrawer ? `${drawerWidth}px` : 0,
          mb: showBottomNav ? `${(theme.mixins as any).bottomNavHeight || 56}px` : 0,
        }}>
        <Box sx={{ maxWidth: 1400, mx: 'auto', width: '100%' }}>
          <Breadcrumb />
          {children}
        </Box>
      </Box>

      {/* Bottom Navigation (mobile/tablet only) */}
      {showBottomNav && (
        <Paper
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.appBar,
          }}
          elevation={3}>
          <BottomNavigation
            value={getCurrentPath()}
            onChange={(_event, newValue) => {
              handleNavigation(newValue)
            }}
            showLabels
            sx={{
              height: (theme.mixins as any).bottomNavHeight || 56,
              '& .MuiBottomNavigationAction-root': {
                minWidth: isMobile ? 72 : 90, // WCAG AAA: min 72x56px
                minHeight: 56,
                padding: isMobile ? '6px 0' : '6px 12px',
              },
            }}>
            {navigationItems
              .filter(item => item.showOnMobile)
              .map(item => (
                <BottomNavigationAction
                  key={item.path}
                  label={item.label}
                  value={item.path}
                  icon={item.icon}
                  sx={{
                    '& .MuiBottomNavigationAction-label': {
                      fontSize: isMobile ? '0.7rem' : '0.75rem',
                      mt: 0.5,
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '1.5rem',
                    },
                  }}
                />
              ))}
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  )
}

export default UserLayout
